// COS-BRIEF-RULES: a brief or Chief of Staff report sent to you, in full — where an inbox item
// of a report opens (`briefs.get`; the server serves only its own recipient).
import type { OperationOutput } from "@companyos/sdk";
import { InfoTip, workerName } from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import type { Api } from "../../api/client";
import { LoadError, Loading } from "../screen-parts";

type Report = OperationOutput<"briefs.get">;
type Item = Report["items"][number];

const SECTION: Record<Item["kind"], string> = {
  kpi: "KPIs",
  task: "Working on",
  run: "Runs",
  blocker: "Stuck",
  ask: "Needs a decision",
};
const ORDER: Item["kind"][] = ["kpi", "task", "run", "blocker", "ask"];

export function BriefReportPage({ api, co, id }: { api: Api; co: string; id: string }) {
  const q = useQuery({
    queryKey: ["company", co, "briefs", id],
    queryFn: () => api.client.briefs.get({ params: { co, id } }),
  });
  if (q.isPending) return <Loading label="Loading the report" rows={6} />;
  if (q.isError) return <LoadError what="The report" error={q.error} />;
  const r = q.data;
  const when = new Date(r.delivered_at ?? r.as_of).toLocaleString();
  return (
    <article className="mx-auto flex w-full max-w-2xl flex-col gap-5 p-4" data-slot="brief-report">
      <header className="flex flex-col gap-1">
        <h1 className="flex items-center gap-1.5 text-lg font-semibold">
          {r.kind === "cos_report" ? (r.for_owner ? "Team update" : "Team report") : "Brief"}
          {r.sections ? (
            <InfoTip label="About this report">
              Only the parts chosen for you: {r.sections.join(", ")}.
            </InfoTip>
          ) : null}
        </h1>
        <p className="text-sm text-muted-foreground">
          From {workerName(r.from.name)} · {when}
        </p>
      </header>
      {r.numbers?.length ? (
        <section className="flex flex-col gap-2" data-slot="brief-numbers">
          <h2 className="flex items-center gap-1.5 text-sm font-medium">
            This week's numbers
            <InfoTip label="About these numbers">
              The same figures Ask answers with; owners and admins only.
            </InfoTip>
          </h2>
          <ul className="flex flex-col gap-1 text-sm">
            {r.numbers.map((l) => (
              <li key={l}>{l}</li>
            ))}
          </ul>
        </section>
      ) : null}
      {r.memory_conflicts ? (
        <section className="flex flex-col gap-2" data-slot="brief-memory-conflicts">
          <h2 className="text-sm font-medium">Memory</h2>
          <p className="text-sm">
            <a className="underline" href={r.memory_conflicts.url}>
              {r.memory_conflicts.open} memory contradiction
              {r.memory_conflicts.open === 1 ? "" : "s"} to settle
            </a>
          </p>
        </section>
      ) : null}
      {r.team?.length ? (
        <section className="flex flex-col gap-2">
          <h2 className="text-sm font-medium">Who is doing what</h2>
          <ul className="flex flex-col gap-1 text-sm">
            {r.team.map((t) => (
              <li key={t.de_membership_id} className="flex justify-between gap-3">
                <span>{workerName(t.name)}</span>
                <span className="text-muted-foreground">
                  {t.doing} in progress
                  {t.blocked ? ` · ${t.blocked} blocked` : ""}
                  {t.asks ? ` · ${t.asks} waiting` : ""}
                  {t.kpis_off ? ` · ${t.kpis_off} KPI off` : ""}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {ORDER.map((k) => {
        const items = r.items.filter((i) => i.kind === k);
        const more = r.more[k] ?? 0;
        if (!items.length && !more) return null;
        return (
          <section key={k} className="flex flex-col gap-2">
            <h2 className="text-sm font-medium">{SECTION[k]}</h2>
            <ul className="flex flex-col gap-1 text-sm">
              {items.map((i) => (
                <li key={`${i.kind}-${i.id}`} className="flex justify-between gap-3">
                  <span>
                    <span className="text-muted-foreground">#{i.n}</span> {i.title}
                  </span>
                  <span className="shrink-0 text-muted-foreground">{i.note}</span>
                </li>
              ))}
              {more ? <li className="text-muted-foreground">and {more} more</li> : null}
            </ul>
          </section>
        );
      })}
    </article>
  );
}
